import {unirValores, valoresDeFiltro} from '@cnc/contracts';
import {useEffect, useId, useRef, useState} from 'react';
import {localeActual, tr, type Texto} from '../i18n/idioma';
import {NotaDeAyuda} from './nota-de-ayuda';

export type OpcionDeFiltro = {valor: string; etiqueta: string; cantidad?: number};

// Filtro acumulable (decision 0018): se marcan uno o varios valores. El valor
// viaja como texto con los elegidos separados por "|"; sin ninguno, "Todos".
// Los elegidos que ya no aparecen en la lista (porque otro filtro los dejo sin
// resultados) se siguen mostrando para poder quitarlos.
export function SelectorMultiple({
  etiqueta,
  valor,
  opciones,
  onCambiar,
  clase = 'block text-xs text-apagado',
  textoVacio,
  onAbrir,
  onBuscar,
  nota,
  ayuda,
}: {
  etiqueta: string;
  valor: string | undefined;
  opciones: OpcionDeFiltro[];
  onCambiar: (valor: string | undefined) => void;
  clase?: string;
  // Lo que se lee sin ninguno marcado (por defecto "Todos"; en una alerta, "Ninguna").
  textoVacio?: string;
  // Filtros por columna (7/10/2026): la lista se pide al abrir y se busca en el servidor.
  onAbrir?: () => void;
  onBuscar?: (texto: string) => void;
  nota?: string;
  // Nota (i) del filtro: que es y como se usa.
  ayuda?: Texto | string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusquedaLocal] = useState('');
  const setBusqueda = (texto: string) => {
    setBusquedaLocal(texto);
    onBuscar?.(texto);
  };
  const caja = useRef<HTMLDivElement>(null);
  const id = useId();
  // Lo marcado se ve al instante, sin esperar a que cambie la direccion.
  const [marcados, setMarcados] = useState<{valor: string | undefined; elegidos: string[]} | null>(null);
  const elegidos = marcados && marcados.valor === valor ? marcados.elegidos : valoresDeFiltro(valor);
  const conocidos = new Set(opciones.map(o => o.valor));
  const todas: OpcionDeFiltro[] = [
    ...elegidos
      .filter(v => !conocidos.has(v))
      .map(v => ({valor: v, etiqueta: `${v} ${tr('(sin resultados)', '(no results)')}`})),
    ...opciones,
  ];
  const visibles = busqueda.trim()
    ? todas.filter(o => o.etiqueta.toLowerCase().includes(busqueda.trim().toLowerCase()))
    : todas;
  const resumen =
    elegidos.length === 0
      ? (textoVacio ?? tr('Todos', 'All'))
      : elegidos.length === 1
        ? (todas.find(o => o.valor === elegidos[0])?.etiqueta ?? elegidos[0])
        : tr(`${elegidos.length} elegidos`, `${elegidos.length} selected`);

  // Se cierra al hacer clic afuera o con Escape.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => e.key === 'Escape' && setAbierto(false);
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);

  const alternar = (v: string) => {
    const nuevos = elegidos.includes(v) ? elegidos.filter(x => x !== v) : [...elegidos, v];
    setMarcados({valor, elegidos: nuevos});
    onCambiar(unirValores(nuevos));
  };

  return (
    <div className={`relative ${clase}`} ref={caja}>
      {/* Nombre e i en el mismo renglon, aunque el contenedor sea una columna. */}
      <span>
        <span id={`${id}-etiqueta`}>{etiqueta}</span>
        {ayuda && <NotaDeAyuda titulo={etiqueta} texto={ayuda} />}
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-labelledby={`${id}-etiqueta`}
        onClick={() => {
          if (!abierto) onAbrir?.();
          setAbierto(x => !x);
        }}
        className={`mt-1 flex w-full items-center justify-between gap-2 rounded-md border bg-white px-2 py-1.5 text-left text-sm text-tinta ${
          elegidos.length ? 'border-verde' : 'border-linea'
        }`}
      >
        <span className="truncate">{resumen}</span>
        <span aria-hidden className="text-apagado">
          ▾
        </span>
      </button>
      {abierto && (
        <div
          role="listbox"
          aria-multiselectable
          aria-label={etiqueta}
          className="absolute z-30 mt-1 w-full min-w-56 rounded-md border border-linea bg-white p-2 text-sm text-tinta shadow-lg"
        >
          {(todas.length > 8 || onBuscar) && (
            <input
              type="search"
              autoFocus
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder={tr('Buscar en la lista', 'Search the list')}
              className="mb-2 w-full rounded-md border border-linea px-2 py-1 text-sm"
            />
          )}
          <ul className="max-h-64 overflow-auto">
            {visibles.map(o => (
              <li key={o.valor}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-papel">
                  <input type="checkbox" checked={elegidos.includes(o.valor)} onChange={() => alternar(o.valor)} />
                  <span className="flex-1">{o.etiqueta}</span>
                  {o.cantidad !== undefined && (
                    <span className="tabular-nums text-xs text-apagado">
                      {o.cantidad.toLocaleString(localeActual())}
                    </span>
                  )}
                </label>
              </li>
            ))}
            {visibles.length === 0 && <li className="px-1 py-1 text-apagado">{tr('Sin opciones', 'No options')}</li>}
          </ul>
          {nota && <p className="mt-1 text-xs text-apagado">{nota}</p>}
          <div className="mt-2 flex justify-between border-t border-linea pt-2 text-xs">
            <button type="button" onClick={() => onCambiar(undefined)} className="text-verde hover:underline">
              {textoVacio ?? tr('Todos', 'All')}
            </button>
            <button type="button" onClick={() => setAbierto(false)} className="font-medium text-verde hover:underline">
              {tr('Listo', 'Done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
